import { faCheck, faHeart } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import Badge from 'components/base/Badge';
import Button from 'components/base/Button';
import Rating from 'components/base/Rating';
import { ProductCat as ProductType } from 'data/e-commerce/products';
import { currencyFormat, PF } from 'helpers/utils';
import { faHeart as farHeart } from '@fortawesome/free-regular-svg-icons';
import { Link } from 'react-router-dom';
import { addToWishlist, removeFromWishlist } from 'api/user/_requests';
import { useDispatch } from 'react-redux';
import {
  addToWishlist as addToWishlistAction,
  removeFromWishlist as removeFromWishlistAction
} from 'redux-state/user/wishlistSlice';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { RootState } from 'redux-state/store';
import { useAppContext } from 'providers/AppProvider';

const ProductCardCat = ({
  product,
  setProduct
}: {
  product: ProductType;
  setProduct: () => void;
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const {
    config: { theme }
  } = useAppContext();
  const handleWishlistClick = async () => {
    if (!user) {
      toast.error('You must be logged in to add to wishlist');
      return;
    }
    if (user.role !== 'customer') {
      toast.error('You must be a customer to add to wishlist');
      return;
    }

    if (product.wishListed) {
      const data = {
        productId: product._id,
        variantId: product.wishlistedVariantId || null
      };
      await removeFromWishlist(data)
        .then(() => {
          dispatch(removeFromWishlistAction(data));
          setProduct();
        })
        .catch(err => {
          toast.error('Failed to remove from wishlist');
        });
    } else {
      const data = {
        productId: product._id,
        variantId: product.wishlistedVariantId || null
      };
      await addToWishlist(data)
        .then(() => {
          dispatch(addToWishlistAction(data));
          setProduct();
        })
        .catch(err => {
          toast.error('Failed to add to wishlist');
        });
    }
  };

  return (
    <div
      className={`position-relative product-card h-100 ${
        theme === 'dark' ? 'bg-dark' : 'bg-white'
      } rounded-3 p-3 shadow-sm`}
    >
      <div className="d-flex flex-column justify-content-between h-100">
        {/* Thumbnail + Wishlist */}
        <div>
          <div className="position-relative mb-3">
            <div className="position-absolute top-0 end-0 m-2 z-2">
              <Button
                variant={product.wishListed ? 'primary' : 'outline-primary'}
                className={classNames(
                  'rounded-circle p-0 d-flex flex-center btn-wish btn-wish-primary  z-2 d-toggle-container',
                  {
                    active: product.wishListed
                  }
                )}
                onClick={handleWishlistClick}
              >
                {product.wishListed ? (
                  <FontAwesomeIcon icon={faHeart} />
                ) : (
                  <>
                    <FontAwesomeIcon icon={faHeart} className="d-block-hover" />
                    <FontAwesomeIcon icon={farHeart} className="d-none-hover" />
                  </>
                )}
              </Button>
            </div>
            <img
              src={PF + (product?.thumbnail ?? 'goods')}
              alt={product.name}
              className="w-100 rounded-2 border border-light-subtle"
              style={{ aspectRatio: '1/1', objectFit: 'cover' }}
            />
            {product.verified && (
              <Badge
                bg="success"
                className="fs-10 position-absolute top-0 start-0 m-2"
              >
                Verified <FontAwesomeIcon icon={faCheck} className="ms-1" />
              </Badge>
            )}
          </div>

          {/* Title */}
          <Link
            to={`/product-details/${product.slug}`}
            className="stretched-link text-decoration-none"
          >
            <h6 className="mb-1 lh-sm text-dark fw-semibold line-clamp-2">
              {product.name}
            </h6>
          </Link>

          {/* Rating */}
          <div className="d-flex align-items-center gap-1 fs-9 mb-2">
            <Rating readonly initialValue={product.totalRanking || 5} />
            <span className="text-body-tertiary">
              ({product.totalReview || 70} rated)
            </span>
          </div>
        </div>

        {/* Bottom Details */}
        <div className="mt-2">
          {product.extra && (
            <p className={classNames(product.extraClass, 'fs-9 mb-1')}>
              {product.extra}
            </p>
          )}
          {product.extra2 && (
            <p className={classNames(product.extra2Class, 'fs-9 mb-1')}>
              {product.extra2}
            </p>
          )}

          {product.discountedPrice && (
            <div className="d-flex align-items-center mb-2">
              {product.price && (
                <p className="me-2 text-muted text-decoration-line-through mb-0">
                  {currencyFormat(product.price)}
                </p>
              )}
              <h5 className="mb-0 text-success">
                {currencyFormat(product.discountedPrice)}
              </h5>
            </div>
          )}

          {/* Meta Info */}
          <div className="d-flex flex-wrap align-items-center justify-content-between">
            {product.colors && (
              <small className="text-body-tertiary fw-semibold">
                {product.colors} colors
              </small>
            )}
            {product.dealEndTime && (
              <small className="text-danger fw-semibold">
                Ends in {product.dealEndTime}
              </small>
            )}
          </div>

          {/* Offer */}
          {product.offer && (
            <span className="badge bg-success-subtle text-success fw-semibold fs-10 mt-2">
              {product.offer} OFF
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCardCat;
